/**
 * Secure Token Storage Service Interface
 *
 * Defines the contract for secure token storage implementations across platforms.
 * This interface ensures consistent behavior while allowing platform-specific
 * secure storage mechanisms (httpOnly cookies for web, secure storage for mobile).
 *
 * Security Features:
 * - Platform-specific secure storage mechanisms
 * - Token expiration validation and automatic cleanup
 * - Refresh token management
 * - Cross-tab/platform synchronization
 * - Memory fallback when secure storage is unavailable
 * - Automatic token cleanup on app initialization
 *
 * @note Types have been migrated to '@shared/types/auth'. Import from there instead.
 */

import {
  DEFAULT_TOKEN_STORAGE_OPTIONS as CENTRALIZED_DEFAULT_OPTIONS,
  DEFAULT_TOKEN_TYPE,
} from '../../types/auth/storage.types';
import { isTokenExpired as isTokenExpiredUtil } from '../../utils/auth/session-utils';
import { dayjs } from '../../utils/date';

// =============================================================================
// TYPES IMPORTED FROM CENTRALIZED LOCATION
// =============================================================================

import type {
  TokenData,
  TokenValidationResult,
  TokenStorageOptions,
} from '../../types/auth/storage.types';

// =============================================================================
// INTERNAL TYPE ALIASES (for use within this file)
// =============================================================================

type TokenDataInternal = TokenData;
type TokenValidationResultInternal = TokenValidationResult;
type TokenStorageOptionsInternal = TokenStorageOptions;

// =============================================================================
// UTILITY FUNCTIONS (using dayjs for date handling)
// =============================================================================

/**
 * Generate storage key with prefix.
 * Re-exported from centralized types for convenience.
 */
export { getStorageKey } from '../../types/auth/storage.types';

/**
 * Parse JWT token to extract expiration time.
 * Re-exported from centralized types for convenience.
 */
export { parseJwtExpiration } from '../../types/auth/storage.types';

/**
 * Check if token data is expired.
 * Uses dayjs for date comparison.
 *
 * @param tokenData - The token data to check
 * @returns True if the token is expired
 */
export const isTokenExpired = (tokenData: TokenDataInternal): boolean => {
  return isTokenExpiredUtil(tokenData.expiresAt);
};

/**
 * Create token validation result.
 * Uses dayjs for date calculations.
 *
 * @param tokenData - The token data to validate
 * @param refreshThreshold - Minutes before expiry to suggest refresh (default: 5)
 * @returns Validation result with expiry status
 */
export const createTokenValidationResult = (
  tokenData: TokenDataInternal | null,
  refreshThreshold: number = 5
): TokenValidationResultInternal => {
  if (!tokenData) {
    return {
      isValid: false,
      isExpired: true,
      expiresIn: 0,
      shouldRefresh: false,
    };
  }

  const isExpired = isTokenExpired(tokenData);
  const expiresAt = dayjs(tokenData.expiresAt);
  const expiresIn = Math.max(0, expiresAt.diff());
  const shouldRefresh = !isExpired && expiresIn <= refreshThreshold * 60 * 1000;

  return {
    isValid: !isExpired,
    isExpired,
    expiresIn,
    shouldRefresh,
  };
};

// =============================================================================
// DEFAULT OPTIONS
// =============================================================================

/**
 * Default token storage options.
 * Re-exported from centralized types for convenience.
 */
export const DEFAULT_TOKEN_STORAGE_OPTIONS: Required<TokenStorageOptionsInternal> =
  CENTRALIZED_DEFAULT_OPTIONS;

/**
 * Default token type.
 * Re-exported from centralized types for convenience.
 */
export const TOKEN_TYPE: TokenDataInternal['tokenType'] = DEFAULT_TOKEN_TYPE;
